/**
 * Newsletter subscription service (public client)
 */

import axios from 'axios';
import apiConfig from '../config/apiConfig';

const API_BASE_URL = apiConfig.BASE_URL;
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';
const SUBSCRIBED_EMAILS_KEY = 'bloomwik_subscribed_emails';
const LEGACY_SUBSCRIBED_EMAILS_KEY = 'blogify_subscribed_emails';
const subscriptionKey = (email) => `bloomwik_subscription_${email.toLowerCase()}`;

function readSubscribedEmails() {
  const raw =
    localStorage.getItem(SUBSCRIBED_EMAILS_KEY) ||
    localStorage.getItem(LEGACY_SUBSCRIBED_EMAILS_KEY);
  return raw ? JSON.parse(raw) : [];
}

const USE_MOCK_API = IS_DEVELOPMENT && process.env.REACT_APP_NEWSLETTER_MOCK === 'true';

function mapSubscribeError(error) {
  const status = error.response?.status;
  const msg = error.response?.data?.message;

  if (status === 400 && msg) {
    return { success: false, message: msg };
  }
  if (status === 409) {
    return {
      success: false,
      message: msg || 'This email is already subscribed to our newsletter.',
    };
  }
  return {
    success: false,
    message: msg || 'Something went wrong. Please try again later.',
  };
}

/**
 * Subscribe to newsletter (double opt-in)
 * @param {string} email
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const subscribeToNewsletter = async (email) => {
  const normalized = String(email || '').trim().toLowerCase();

  if (USE_MOCK_API) {
    await new Promise((resolve) => setTimeout(resolve, 800));

    const subscribedEmails = readSubscribedEmails();
    if (subscribedEmails.includes(normalized)) {
      return {
        success: false,
        message: 'This email is already subscribed to our newsletter.',
      };
    }

    const token = `mock_token_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    subscribedEmails.push(normalized);
    localStorage.setItem(SUBSCRIBED_EMAILS_KEY, JSON.stringify(subscribedEmails));
    localStorage.setItem(
      subscriptionKey(normalized),
      JSON.stringify({
        email: normalized,
        token,
        status: 'pending',
        subscribedAt: new Date().toISOString(),
      })
    );

    const confirmationUrl = `${window.location.origin}/confirm-subscription?token=${token}`;
    console.info('[newsletter mock] Confirmation link:', confirmationUrl);

    return {
      success: true,
      message:
        'Thank you for subscribing! Please check your inbox to confirm. (Dev mock: see console for confirm link)',
      token,
    };
  }

  try {
    const response = await axios.post(`${API_BASE_URL}/newsletter/subscribe`, { email: normalized });
    return {
      success: true,
      message:
        response.data.message ||
        'Thank you for subscribing! Please check your inbox to confirm your subscription.',
    };
  } catch (error) {
    if (!error.response) {
      return {
        success: false,
        message: 'Unable to connect to the server. Please check your connection and try again.',
      };
    }
    return mapSubscribeError(error);
  }
};

/**
 * Unsubscribe via token from email link
 * @param {string} token
 */
export const unsubscribeFromNewsletter = async (token) => {
  if (USE_MOCK_API) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return {
      success: true,
      message: 'You have been unsubscribed. (Dev mock)',
    };
  }

  try {
    const response = await axios.get(`${API_BASE_URL}/newsletter/unsubscribe/${token}`);
    return {
      success: true,
      message: response.data.message || 'You have been successfully unsubscribed.',
    };
  } catch (error) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        'Unable to process your request. Please try again later.',
    };
  }
};

/**
 * Confirm subscription (double opt-in)
 * @param {string} token
 */
export const confirmSubscription = async (token) => {
  if (USE_MOCK_API) {
    await new Promise((resolve) => setTimeout(resolve, 600));

    let foundSubscription = null;
    let foundEmail = null;

    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || (!key.startsWith('bloomwik_subscription_') && !key.startsWith('blogify_subscription_'))) {
        continue;
      }
      try {
        const subscription = JSON.parse(localStorage.getItem(key));
        if (subscription.token === token) {
          foundSubscription = subscription;
          foundEmail = key.replace(/^bloomwik_subscription_/, '').replace(/^blogify_subscription_/, '');
          break;
        }
      } catch {
        /* skip */
      }
    }

    if (!foundSubscription) {
      return {
        success: false,
        message: 'Invalid or expired confirmation link. Please subscribe again.',
      };
    }

    if (foundSubscription.status === 'confirmed') {
      return {
        success: true,
        message: 'Your subscription was already confirmed!',
      };
    }

    foundSubscription.status = 'confirmed';
    foundSubscription.confirmedAt = new Date().toISOString();
    localStorage.setItem(subscriptionKey(foundEmail), JSON.stringify(foundSubscription));

    return {
      success: true,
      message: 'Your subscription has been confirmed! You will now receive our latest articles and updates.',
    };
  }

  try {
    const response = await axios.get(`${API_BASE_URL}/newsletter/confirm/${token}`);
    return {
      success: true,
      message: response.data.message || 'Your subscription has been confirmed!',
    };
  } catch (error) {
    const msg = error.response?.data?.message;
    if (error.response?.status === 400 && msg?.toLowerCase().includes('already confirmed')) {
      return { success: true, message: msg };
    }
    return {
      success: false,
      message: msg || 'Invalid or expired confirmation link.',
    };
  }
};
